<template>
  <lf-card class="mb-8 py-6" data-cy="tax-return-panel">
    <template #header>
      <div class="flex w-full flex-col space-y-3">
        <div class="flex w-full flex-none items-center justify-between">
          <div class="flex justify-start space-x-2">
            <lf-h3 data-cy="tax-return-title">
              {{ $t("TAX_RETURN.BUSINESS.TITLE") }}
            </lf-h3>
            <counter-pill :count="fieldData.length" color="#3769D6" />
          </div>
          <div v-if="canEditDealProgress">
            <business-tax-return-modal
              v-if="isModalOpen"
              :data="activeData"
              :combined-fields="COMBINED_FIELDS"
              :is-new="isNew"
              :close="() => (isModalOpen = false)"
              data-cy="modal"
            />
            <more-button data-cy="tax-return-more-button">
              <ul>
                <li
                  v-if="activeData"
                  data-cy="edit-tax-return-button"
                  @click="initEditTaxReturn"
                >
                  {{ $t("COMMON.EDIT") }}
                </li>
                <li data-cy="add-tax-return-button" @click="initNewTaxReturn">
                  {{ $t("INDIVIDUAL_PROFILES.ADD_TAX_RETURN") }}
                </li>
                <li
                  v-if="canDeleteBusinessTaxReturn && activeData"
                  class="text-red-500"
                  @click="showDeleteModal = true"
                >
                  {{ $t("COMMON.DELETE") }}
                </li>
              </ul>
            </more-button>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(year, i) in years"
            :key="year"
            class="cursor-pointer rounded-sm border p-2 text-xs"
            :class="
              indexOfActiveTab === i
                ? 'border-transparent bg-primary/10 text-primary hover:bg-primary/15'
                : 'border-gray-200 bg-white text-gray-400'
            "
            :data-cy="`tax-return-tab-${year}`"
            @click="setActiveTabIndex(i)"
          >
            {{ year }}
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <loader :is-loading="updating" />
      <div v-if="activeData" class="grid grid-cols-3 gap-5">
        <div
          v-for="field in COMBINED_FIELDS"
          :key="`${activeData?.business_tax_return_information_id ?? ''}-${field?.name}`"
          class="grid-element"
          :class="{
            'col-span-3 border-t': field.name === DIVIDER,
            'col-span-3': field.name === OTHER_DEDUCTIONS_DETAIL_FIELD,
          }"
        >
          <div
            v-if="field.name === OTHER_DEDUCTIONS_DETAIL_FIELD"
            class="flex flex-wrap gap-y-4"
          >
            <workflow-field
              v-for="deduction in field.value"
              :id="field?.name?.replaceAll('_', '-')"
              :key="deduction"
              class="deductions-detail"
              :label="get(deduction, 'type', '')"
              :value="get(deduction, 'amount', '-')"
              click-to-copy
            />
          </div>
          <workflow-field
            v-else-if="field.name !== DIVIDER"
            :id="field?.name?.replaceAll('_', '-')"
            :key="`${activeData?.business_tax_return_information_id ?? ''}-${field?.name}`"
            :data-cy="`field-${field?.name?.replaceAll('business_tax_return_', '')}`"
            :label="field?.placeholder || ''"
            :value="field?.displayValue || field?.value || ''"
            click-to-copy
          />
          <div
            v-else-if="field.placeholder"
            class="pt-2"
            :data-cy="`divider-${field?.name}`"
          >
            <lf-h3>{{ field.placeholder }}</lf-h3>
          </div>
        </div>
      </div>
      <div v-else>
        {{ $t("COMMON.NO_RESULTS") }}
      </div>
    </template>
  </lf-card>
  <confirm-modal
    v-if="showDeleteModal"
    :title="$t('DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.DELETE')"
    :description="$t('DEALS.DEAL_PROGRESS.BUSINESS_TAX_INFO.DELETE_BODY')"
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="deleteTaxReturn"
  />
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useStore } from "vuex";

import get from "lodash/get";

import { WORKFLOW_BLOCKS_IDS } from "@/helpers/constants/workflow";
import { useDataCollectionCanEdit } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useBusinessTaxReturn } from "@/hooks/taxReturns";

import CounterPill from "@/components/CounterPill.vue";
import WorkflowField from "@/components/deals/WorkflowLive/WorkflowLiveField.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import LfH3 from "@/components/ui/text/LfHeading3.vue";
import BusinessTaxReturnModal from "@/views/deals/components/BusinessTaxReturnModal.vue";
import MoreButton from "@/views/deals/components/MoreButton.vue";

const DIVIDER = "divider" as const;
const OTHER_DEDUCTIONS_DETAIL_FIELD =
  "business_tax_return_other_deductions_detail" as const;

const { canEditDealProgress } = useDeals();
const canDeleteBusinessTaxReturn = useDataCollectionCanEdit();
const {
  fieldData,
  indexOfActiveTab,
  activeData,
  years,
  COMBINED_FIELDS,
  setActiveTabIndex,
  updating,
} = useBusinessTaxReturn();
const { dispatch } = useStore();

const isNew = ref(false);
const isModalOpen = ref(false);
const showDeleteModal = ref(false);

const initEditTaxReturn = () => {
  isNew.value = false;
  isModalOpen.value = true;
};

const initNewTaxReturn = () => {
  setActiveTabIndex(null);
  isNew.value = true;
  isModalOpen.value = true;
};

const { fetchWrapper: deleteTaxReturn } = usePromiseWrapper(async () => {
  if (!activeData.value) {
    return;
  }

  const payloadFields = {
    business_tax_return_information_id:
      activeData.value.business_tax_return_information_id,
    _delete: true,
  };

  const payload = {
    [WORKFLOW_BLOCKS_IDS.business_tax_return]: [payloadFields],
  };

  await dispatch("workflows/updateWorkflowSnapshotFields", payload);
});

watch(fieldData, (newValue, oldValue) => {
  if (newValue.length !== oldValue.length) {
    setActiveTabIndex(fieldData.value.length - 1);
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep() {
  .deductions-detail {
    @apply w-1/3;
  }
}
</style>
